import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { maskLine } from "./motion";
import { IMAGES, HERO, CONTACT_EMAIL } from "../../lib/siteData";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.4, 0.85]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden bg-ink">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <motion.img
          src={IMAGES.hero}
          alt="Floodlit grassroots football pitch at night"
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1] bg-black" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-ink via-ink/40 to-transparent" />

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 flex min-h-[100svh] flex-col justify-end pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-8 flex items-center gap-3 font-body text-xs font-bold uppercase tracking-[0.25em] text-brand"
        >
          <span className="h-px w-10 bg-brand" />
          {HERO.overline}
        </motion.div>

        <h1 className="font-heading font-bold uppercase leading-[0.86] tracking-tighter text-white">
          {HERO.title.map((line, i) => (
            <span key={i} className="block overflow-hidden py-0.5">
              <motion.span
                custom={i}
                variants={maskLine}
                initial="hidden"
                animate="show"
                className={`block text-5xl sm:text-7xl lg:text-[8rem] ${i === 2 ? "text-brand" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-xl font-body text-lg font-light leading-relaxed text-neutral-300">
            {HERO.body}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="hero-primary-cta"
              className="group inline-flex w-fit items-center gap-3 rounded-full bg-brand px-7 py-4 font-body text-sm font-semibold uppercase tracking-wide text-ink transition-colors duration-300 hover:bg-brand-hover"
            >
              {HERO.primary.label}
              <span className="grid h-6 w-6 place-items-center rounded-full bg-black/20 transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
            <a
              href={HERO.secondary.href}
              data-testid="hero-secondary-cta"
              className="inline-flex w-fit items-center gap-2 rounded-full border border-white/25 px-7 py-4 font-body text-sm font-semibold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-white hover:text-black"
            >
              {HERO.secondary.label}
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 flex items-center gap-2 font-body text-xs uppercase tracking-[0.2em] text-neutral-500"
      >
        Scroll
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
